/*    
<summary>
  This TypeScript file defines constant option arrays for business types, store categories, registration types, and user types.
  These constants are used for populating dropdowns and selection fields in the UI.
  Developer: Mohammad Saquib Khan, Created Date: 02-April-2024
</summary>
*/

import { IOption, IUserTypeOption } from "../models/ICommon";

/**
 * List of business type options for selection fields.
 * Each option includes an `id`, a string `value`, and an optional `disabled` flag.
 *
 * @constant
 * @type {IOption[]}
 *
 * @example
 * // Example usage:
 * businessTypeOptions.find(option => option.value === "Massage");
 */
export const businessTypeOptions: IOption[] = [
  { id: -1, value: "please_select", disabled: true },
  { id: 0, value: "Footcare" },
  { id: 1, value: "Massage" },
  { id: 2, value: "Yoga" },
  { id: 3, value: "Spa" },
];

/**
 * List of store category options for dropdowns.
 * Each option includes an `id`, a string `value`, and an optional `disabled` flag.
 *
 * @constant
 * @type {IOption[]}
 *
 * @example
 * // Example usage:
 * storeCategoryOptions.find(option => option.value === "fc_store");
 */
export const storeCategoryOptions: IOption[] = [
  { id: -1, value: "please_select", disabled: true },
  { id: 0, value: "fc_store" },
  { id: 1, value: "dm_store" },
];

/**
 * List of registration type options for user registration forms.
 * Each option includes an `id` and a string `value`.
 *
 * @constant
 * @type {IOption[]}
 *
 * @example
 * // Example usage:
 * registrationTypeOptions.find(option => option.value === "Store");
 */
export const registrationTypeOptions: IOption[] = [
  { id: -1, value: "please_select" },
  { id: 0, value: "Headquarter" },
  { id: 1, value: "Store" },
];

/**
 * List of user type options used throughout the application.
 * Each option includes an `id`, a string `value` representing the user type,
 * and an `icon` identifier for UI representation.
 *
 * @constant
 * @type {IUserTypeOption[]}
 *
 * @example
 * // Example usage:
 * userTypeOptions.find(option => option.value === "Staff");
 */
export const userTypeOptions: IUserTypeOption[] = [
  { id: 0, value: "SuperAdmin", icon: "person-gear" },
  { id: 1, value: "Customer", icon: "person-badge" },
  { id: 2, value: "Staff", icon: "people" },
  { id: 3, value: "Store", icon: "person-add" },
  { id: 4, value: "Group", icon: "person-lock" },
  { id: 5, value: "HeadQuarter", icon: "building" },
];

export const minuteIntervalOptions: IOption[] = [
  { id: -1, value: "please_select" }, 
  { id: 0, value: "5" },
  { id: 1, value: "10" },
  { id: 2, value: "15" },
  { id: 3, value: "20" },
  { id: 4, value: "30" }
];

export const ageGroupOptions: IOption[] = [
  { id: -1, value: "please_select" }, 
  { id: 10, value: "Under10Year" },
  { id: 20, value: "Teenager" },
  { id: 30, value: "Twenties" },
  { id: 40, value: "Thirties" },
  { id: 50, value: "Forties" },
  { id: 60, value: "Fifties" },
  { id: 70, value: "Sixties" },
  { id: 80, value: "Seventies" },
  { id: -2, value: "NotSpecified" } // In case for not specified
];
